import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { PublisherLogo } from "../publisher/PublisherLogo";
import { StaticListItemUrl } from "../core/EditableListItemUrl";
import { IsMember, LockedButton, LockedListItem } from "../core/HasEntitlements";
import { Now } from "../core/Now";
import { SupportEmail } from "../core/CommonLinks";
import { isStale } from "@seabrookstudios/pitch2table-core";
import { PublisherTimezoneWarning } from "../games/PublisherTimezoneWarning";
import Button from "@mui/material/Button";
import CardActions from "@mui/material/CardActions";
import { Link } from "react-router-dom";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Stack from "@mui/material/Stack";
import { addUtmSource } from "../util/add-utm";
import { PublisherAttendedConventionChips } from "./PublisherAttendedConventionChips";
import { PublisherRoleChips } from "./PublisherRoleChips";
import { usePublisherGuidelines } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Markdown from "react-markdown";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Publisher} props.publisher
 */
const PublisherDNAButton = ({ publisher }) => {
  const { data: guidelines } = usePublisherGuidelines(publisher.id);

  const CustomControl = () => {
    return (
      <Button variant="contained" color="primary">
        Publisher DNA
      </Button>
    );
  };

  return (
    guidelines &&
    guidelines.length > 0 && (
      <LockedButton lockedLabel="PublisherDNA" variant="contained">
        <FullScreenDialog title="Publisher DNA" CustomControl={<CustomControl />}>
          <Markdown>{guidelines}</Markdown>
        </FullScreenDialog>
      </LockedButton>
    )
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherForApp} props.publisher
 */
export const PublisherDetailCard = ({ publisher }) => {
  return (
    <Card elevation={1} square={false}>
      <Box p={2}>
        <PublisherLogo url={publisher.logoUrl} name={publisher.name} />
        <Box pt={2}>
          <Typography variant="h5" textAlign="left" color="text.secondary" noWrap>
            {publisher.name}
          </Typography>
          <PublisherRoleChips publisherRole={publisher.publisherRole} />
          {publisher.alternateNames.length > 0 && (
            <ListItem>
              <ListItemText primary="Also Known As" secondary={publisher.alternateNames.join(", ")} />
            </ListItem>
          )}
        </Box>
        <List>
          {publisher.defunct && (
            <Alert severity="error">
              Based on our latest information, this publisher is believed to be defunct. If this is incorrect please
              contact <SupportEmail />.
            </Alert>
          )}
          <IsMember LockedView={<LockedListItem title="Accepting Submissions" />}>
            {publisher.acceptingSubmissions === false && publisher.defunct !== true && (
              <Alert severity="warning">Publisher is currently not accepting submissions.</Alert>
            )}
          </IsMember>
          {publisher.website && <StaticListItemUrl title="Website" text={publisher.website} />}
          {publisher.contact && (
            <IsMember LockedView={<LockedListItem title="Contact Information" />}>
              {publisher.contact.includes("@") ? (
                <ListItem>
                  <ListItemText primary="Contact" secondary={publisher.contact} />
                </ListItem>
              ) : (
                <StaticListItemUrl title="Contact" text={publisher.contact} />
              )}
            </IsMember>
          )}
          {publisher.timezone && publisher.country && (
            <ListItem>
              {publisher.country && <ListItemText primary="Country" secondary={publisher.country} />}
              {publisher.timezone && <ListItemText primary="Timezone" secondary={publisher.timezone} />}
              {publisher.timezone && (
                <ListItemText primary="Current Time" secondary={<Now timezone={publisher.timezone} />} />
              )}
            </ListItem>
          )}
          {publisher.timezone && <PublisherTimezoneWarning timezone={publisher.timezone} />}
          <Stack direction="row" spacing={1}>
            {publisher.yearLastGameReleased && (
              <ListItem>
                <ListItemText primary="Year Last Game Released" secondary={publisher.yearLastGameReleased} />
              </ListItem>
            )}
            <IsMember LockedView={<LockedListItem title="Games Published (including expansions)" />}>
              {publisher.gamesCount && publisher.gamesCount !== "" && (
                <ListItem>
                  <ListItemText primary="Games Published (including expansions)" secondary={publisher.gamesCount} />
                </ListItem>
              )}
            </IsMember>
          </Stack>
          <IsMember LockedView={<LockedListItem title="Conventions Attended" />}>
            {publisher.attendedConventions?.length > 0 && (
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary="Conventions Attended"
                  secondary={<PublisherAttendedConventionChips conventions={publisher.attendedConventions} />}
                />
              </ListItem>
            )}
          </IsMember>
          {isStale(publisher) && (
            <Alert severity="warning">
              Please note this publisher has not published a game since {publisher.yearLastGameReleased}.
            </Alert>
          )}
        </List>
      </Box>
      <CardActions>
        {publisher.bgg && (
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to={addUtmSource(publisher.bgg)}
            target="_blank"
            endIcon={<OpenInNew />}
          >
            BGG
          </Button>
        )}
        {publisher.submissionsLink && (
          <LockedButton lockedLabel="Submissions Page" variant="outlined">
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to={addUtmSource(publisher.submissionsLink)}
              target="_blank"
              endIcon={<OpenInNew />}
            >
              Submissions Page
            </Button>
          </LockedButton>
        )}
        <PublisherDNAButton publisher={publisher} />
      </CardActions>
    </Card>
  );
};
